import React from 'react';
import { useFormContext } from 'react-hook-form';

import Button from '$/components/ui/Button';
import { CircularDeleteButton } from '$/components/ui/Button/Button';
import { DragContainer, Draggable } from '$/components/ui/DragAndDrop';
import Form from '$/components/ui/Form';
import Checkbox from '$/components/ui/Input/Basics/Checkbox';
import Dropdown from '$/components/ui/Input/Basics/Dropdown';
import Input from '$/components/ui/Input/Basics/Input';
import PageBuilder from '$/components/ui/Input/PageBuilder';
import { PageMode } from '$/components/ui/Input/PageBuilder.types';
import DefinePropertyDropdown from '$/components/ui/Input/PropertyDropdown';
import { useThemeContext } from '$/components/ui/ThemeContext';
import {
  OptionField,
  RegistrationFieldType,
  RegistrationFormField,
  SelectionType,
} from '$/GraphQL/schema/schema';
import { langDirClassname } from '$/shared/frontend/utils/i18n';
import {
  getAttendeeFieldName,
  getAttendeeFieldValueFieldName,
  getFieldName,
} from '$/utils/attendeeFormField';
import { ClassNames, css } from '@emotion/react';
import tw from 'twin.macro';
import { usePageEditorContext } from '../PageEditorContext';
import { useFieldTypeOptions } from './useFieldTypeOptions';
import { RenderFieldProps, UseRenderFieldProps } from './RegistrationForm.types';

const DropdownOptionsPreview: React.FC<{ options?: OptionField[] | null }> = ({ options }) => {
  const etheme = useThemeContext();

  if (!options || (options && options.length === 0)) {
    options = ['1', '2', '3'].map((val) => {
      return {
        text: `Choice #${val}`,
        value: val,
      };
    });
  }

  return (
    <div className='flex flex-col items-end'>
      {options.slice(0, 3).map((option, index) => {
        return (
          <div
            key={index}
            className='flex items-center justify-end opacity-70 rounded-lg w-1/2 h-2_25e mt-1/2e px-1e'
            css={css`
              background: ${etheme.home.page.bg};
            `}
          >
            {option.text}
          </div>
        );
      })}
    </div>
  );
};

export const useRenderField = ({
  country,
  i18n,
  draggingIndex,
  isInVideoRegistration,
  setFieldEditing,
  setCountry,
}: UseRenderFieldProps) => {
  const state = usePageEditorContext();
  const { watch, setValue, setError, clearErrors } = useFormContext();
  const fieldTypeOptions = useFieldTypeOptions(country, i18n);
  const { mode } = state;
  const dir = mode === PageMode.edit ? 'ltr' : langDirClassname(state.language);

  const getComponentFromFieldType = React.useCallback(
    (type?: RegistrationFieldType): JSX.Element => {
      if (!type) {
        return <Input />;
      }
      return fieldTypeOptions.find((e) => e.value === type)!.component;
    },
    [fieldTypeOptions]
  );

  const renderField = ({
    id,
    fieldName,
    formField,
    fieldIndex,
    attendeeFieldName,
    attendeeValueFieldName,
    typeOptions = fieldTypeOptions,
    inputRef,

    hidden,
    disabled,
    draggable,
    canChangeType,
    isLastNameField,
    showPropertyInput,
    excludeProperties,

    hideNote,
    placeholder,

    onRemove,
  }: RenderFieldProps): React.ReactElement | null => {
    fieldName = fieldName ?? getFieldName(fieldIndex);
    attendeeFieldName = attendeeFieldName ?? getAttendeeFieldName(fieldIndex);
    attendeeValueFieldName = attendeeValueFieldName ?? getAttendeeFieldValueFieldName(fieldIndex);
    const type: RegistrationFormField['type'] = watch(`${fieldName}.type`) ?? formField?.type;
    const note: RegistrationFormField['note'] = watch(`${fieldName}.note`) ?? formField?.note;
    const subType: RegistrationFormField['subType'] =
      watch(`${fieldName}.subType`) ?? formField?.subType;
    const options: RegistrationFormField['options'] =
      watch(`${fieldName}.options`) ?? formField?.options;
    const isRemovable = watch(`${fieldName}.isRemovable`) ?? formField?.isRemovable;
    const propertyName = watch(`${fieldName}.propertyName`) ?? formField?.propertyName;

    const isHiddenField = type === RegistrationFieldType.Hidden;
    const hideIfHiddenFieldClassName = (className?: string): string =>
      `${isHiddenField ? 'hidden' : ''} ${className ?? ''}`;

    const wrapperClassName = `
    reg-field overflow-visible
    ${mode !== PageMode.edit && isHiddenField && 'hidden'}
    ${hidden ? 'hidden mb-0' : ''}
  `;

    return (
      <ClassNames>
        {({ css, cx }): React.ReactElement => {
          return (
            <Draggable disabled={!draggable} className={wrapperClassName} key={id}>
              <div
                className='flex relative'
                css={css`
                  ${mode === PageMode.edit
                    ? `
                      margin-left: -2rem;
                      padding-left: 2rem;
                      `
                    : ''}
                  .drag-handle {
                    visibility: hidden;
                  }
                  &:hover {
                    .drag-handle {
                      visibility: visible;
                    }
                  }
                `}
              >
                <div
                  className={`
                    ${draggable && draggingIndex === undefined ? '' : 'hidden'}
                    drag-handle absolute h-full border-gray-500
                  `}
                  css={css`
                    cursor: grab;
                    margin: 0 0.75rem;
                    width: 11px;
                    height: 100%;
                    left: 0;
                    max-height: 4.5rem;
                    border-left: 4px dotted;
                    border-right: 4px dotted;
                    border-image-repeat: repeat;
                    padding: 0px 1px;
                  `}
                />
                <div
                  className={`flex-1`}
                  css={css`
                    .EditableInput {
                      border-width: 1px;
                    }
                  `}
                >
                  <div className='flex justify-between items-end leading-tight pb-1'>
                    {/* field w/o optional */}
                    <div className={hideIfHiddenFieldClassName('relative text-md')}>
                      <Form.RawField
                        name={`${fieldName}.isRemovable`}
                        defaultValue={formField?.isRemovable}
                        type='hidden'
                        component={<Input />}
                        className='hidden'
                      />

                      <Form.RawField
                        name={`${fieldName}.type`}
                        defaultValue={formField?.type}
                        type='hidden'
                        component={<Input />}
                        className='hidden'
                      />

                      {mode !== PageMode.edit && (
                        <Form.RawField
                          name={`${fieldName}.isRequired`}
                          type='hidden'
                          component={<Input />}
                          className='hidden'
                        />
                      )}

                      <Form.Field
                        name={`${fieldName}.fieldName`}
                        className={cx(
                          'outline-none font-normal',
                          css`
                            margin-right: 0.25rem;
                            max-width: ${mode !== PageMode.edit ? '100%' : '20.75rem'};
                            margin-bottom: 0;
                            left: 0;
                          `
                        )}
                        defaultValue={formField?.fieldName}
                        component={
                          mode !== PageMode.edit ? (
                            <label
                              htmlFor={attendeeFieldName}
                              className={`text-color-input-field ${isInVideoRegistration ? 'hidden' : ''}`}
                            >
                              {watch(`${fieldName}.fieldName`)}
                              {watch(`${fieldName}.isRequired`) && (
                                <span
                                  className={cx(
                                    'text-lg text-coral-1',
                                    css`
                                      margin-left: 3px;
                                    `
                                  )}
                                >
                                  *
                                </span>
                              )}
                            </label>
                          ) : !isHiddenField ? (
                            <PageBuilder.TextEdit
                              state={state}
                              placeholder='Enter question here'
                              showInlineToolbar={false}
                            />
                          ) : (
                            <Input type='hidden' />
                          )
                        }
                      />

                      {mode === PageMode.edit && watch(`${fieldName}.isRequired`) && (
                        <span
                          className={cx(
                            'text-lg text-coral-1 absolute',
                            css`
                              right: -6px;
                              top: -2px;
                            `
                          )}
                        >
                          *
                        </span>
                      )}
                    </div>

                    <div className='flex items-center'>
                      {mode === PageMode.edit && (
                        <div className={hideIfHiddenFieldClassName('text-md text-gray-1')}>
                          <Form.RawField
                            defaultValue={formField?.isRequired}
                            name={`${fieldName}.isRequired`}
                            className={!isRemovable && !isLastNameField ? 'hidden' : ''}
                            css={css`
                              margin-bottom: 0.1em;
                              label {
                                font-size: 0.75em;
                              }
                            `}
                            component={
                              <Checkbox
                                css={css`
                                  label {
                                    ${tw`text-blue-3!`}
                                  }
                                `}
                                label='Required'
                                labelLeft
                              />
                            }
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* input w/o cross */}
                  <div className='relative'>
                    {mode === PageMode.edit && isRemovable && (
                      <CircularDeleteButton
                        onClick={onRemove}
                        className='absolute flex mt-3 -mr-3 h-5 w-5'
                      />
                    )}
                    <div className='flex relative'>
                      {mode !== PageMode.edit && (
                        <Form.Field
                          name={attendeeValueFieldName}
                          defaultValue={
                            type === RegistrationFieldType.Countries
                              ? country.toUpperCase()
                              : subType === SelectionType.Multi
                                ? []
                                : ''
                          }
                          onChange={(v): void => {
                            if (type === RegistrationFieldType.Countries) {
                              setCountry(v);
                            }
                          }}
                          className={`opacity-100 w-full`}
                          css={css`
                            .dropdown a {
                              text-decoration: none;
                            }
                          `}
                          component={React.cloneElement(getComponentFromFieldType(type), {
                            type: type,
                            disabled,
                            inputRef,
                            options: options?.map(({ text, value }) => ({
                              text,
                              value: value ? value : text, // use text if value is blank
                            })),
                            multiple: subType === SelectionType.Multi ? true : undefined,
                            placeholder: isInVideoRegistration
                              ? watch(`${fieldName}.fieldName`)
                              : undefined,
                          })}
                        />
                      )}
                      {mode === PageMode.edit && (
                        <>
                          {placeholder ? <Input disabled={true} placeholder={placeholder} /> : null}
                          <Form.Field
                            name={`${fieldName}.propertyName`}
                            className={`
                              ${placeholder ? 'hidden' : ''}
                              ${cx(
                                `opacity-100 w-full`,
                                css`
                                  .dropdown.icon {
                                    visibility: hidden !important;
                                  }
                                  .active {
                                    .dropdown.icon {
                                      visibility: visible !important;
                                    }
                                  }
                                `
                              )}`}
                            defaultValue={formField?.propertyName}
                            component={
                              showPropertyInput ? (
                                <Input
                                  disabled={true}
                                  placeholder={propertyName ?? undefined}
                                  value={undefined}
                                />
                              ) : (
                                <DefinePropertyDropdown
                                  id={`propertyName-${fieldIndex}`}
                                  defaultProperty={formField?.propertyName}
                                  defaultTitle={watch(`${fieldName}.fieldName`)}
                                  placeholder='Enter property name here'
                                  excludeProperties={excludeProperties}
                                  onError={(error): void => {
                                    setError(`${fieldName}.propertyName`, {
                                      type: 'error',
                                      message: error,
                                    });
                                  }}
                                  onModified={(): void => {
                                    clearErrors(`${fieldName}.propertyName`);
                                  }}
                                  isCreating={!formField?.fieldName && !formField?.propertyName}
                                />
                              )
                            }
                          />
                          <Form.Field
                            name={`${fieldName}.type`}
                            type='hidden'
                            defaultValue={formField?.type}
                            component={<Input type='hidden' />}
                          />
                          {canChangeType && (
                            <div
                              css={css`
                                ${tw`text-blue-3`}
                                position: absolute;
                                right: 1.75rem;
                                top: 0.75em;
                                margin-bottom: 0 !important;
                                .ui.dropdown {
                                  display: flex !important;
                                  align-items: baseline;
                                  .dropdown.icon {
                                    &:before {
                                      transform: translate(-10%, -10%) scale(1.25, 1) !important;
                                    }
                                  }
                                }
                              `}
                            >
                              <Dropdown
                                disabled={mode !== PageMode.edit}
                                selection={false}
                                floating
                                upward
                                direction='left'
                                options={typeOptions}
                                value={watch(`${fieldName}.type`)}
                                onChange={(e): void => {
                                  setValue(`${fieldName}.type`, e, { shouldDirty: true });
                                  if (formField) formField.type = e;
                                }}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    <div className={hideIfHiddenFieldClassName('relative text-md')}>
                      <Form.Field
                        className={`font-italic mt-2 opacity-70 ${hideNote ? 'hidden' : ''}`}
                        name={`${fieldName}.note`}
                        defaultValue={formField?.note ?? note ?? ''}
                        component={
                          type !== RegistrationFieldType.Hidden ? (
                            <PageBuilder.TextEdit
                              state={state}
                              placeholder='Add a field note'
                              disabled={mode !== PageMode.edit}
                              isRtl={dir === 'rtl'}
                              showInlineToolbar={true}
                              className={mode !== PageMode.edit ? 'border-0' : ''}
                            />
                          ) : (
                            <Input type='hidden' />
                          )
                        }
                      />
                    </div>

                    <Form.RawField
                      name={`${attendeeFieldName}.fieldName`}
                      defaultValue={formField?.fieldName}
                      type='hidden'
                      component={<Input />}
                      className='hidden'
                    />
                    <Form.RawField
                      name={`${attendeeFieldName}.propertyName`}
                      defaultValue={formField?.propertyName}
                      type='hidden'
                      component={<Input />}
                      className='hidden'
                    />
                    <Form.RawField
                      name={`${attendeeFieldName}.type`}
                      defaultValue={formField?.type}
                      type='hidden'
                      component={<Input />}
                      className='hidden'
                    />
                    {type === RegistrationFieldType.Selection && (
                      <Form.RawField
                        name={`${attendeeFieldName}.subType`}
                        defaultValue={formField?.subType}
                        type='hidden'
                        component={<Input />}
                        className='hidden'
                      />
                    )}
                    <Form.RawField
                      name={`${attendeeFieldName}.isRequired`}
                      defaultValue={formField?.isRequired}
                      type='hidden'
                      component={<Input />}
                      className='hidden'
                    />
                    <Form.RawField
                      name={`${attendeeFieldName}.isRemovable`}
                      defaultValue={formField?.isRemovable}
                      type='hidden'
                      component={<Input />}
                      className='hidden'
                    />

                    <Form.RawField
                      name={`${fieldName}.options`}
                      defaultValue={formField?.options}
                      type='hidden'
                      component={<Input />}
                      className='hidden'
                    />

                    <Form.RawField
                      name={`${fieldName}.subType`}
                      defaultValue={formField?.subType}
                      type='hidden'
                      component={<Input />}
                      className='hidden'
                    />

                    {mode === PageMode.edit && type === RegistrationFieldType.Selection && (
                      <div
                        className='cursor-pointer'
                        onClick={(): void => {
                          const options = watch(`${fieldName}.options`) ?? formField?.options;
                          setFieldEditing?.({
                            fieldIndex: fieldIndex,
                            fieldName: watch(`${fieldName}.fieldName`),
                            subType: watch(`${fieldName}.subType`),
                            options: options
                              ? options.filter((option: OptionField) => option.text)
                              : undefined,
                          });
                        }}
                      >
                        <DropdownOptionsPreview options={formField?.options ?? options} />
                        <div className='flex justify-end mt-3/4e mb-1e'>
                          <Button.blueText>Edit selector choices</Button.blueText>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Draggable>
          );
        }}
      </ClassNames>
    );
  };

  return renderField;
};

export const useRenderFields = ({
  country,
  i18n,
  isInVideoRegistration,
  setFieldEditing,
  setCountry,
}: UseRenderFieldProps) => {
  const renderField = useRenderField({
    country,
    i18n,
    isInVideoRegistration,
    setFieldEditing,
    setCountry,
  });

  const renderFields = React.useCallback(
    ({
      builtInFields,
      draggableFields,
      hiddenFields,
      onMove,
      setDraggingIndex,
    }: {
      builtInFields: RenderFieldProps[];
      draggableFields: RenderFieldProps[];
      hiddenFields: RenderFieldProps[];
      onMove: ({
        removedIndex,
        addedIndex,
      }: {
        removedIndex: number | null;
        addedIndex: number | null;
      }) => void;
      setDraggingIndex: (index: number | undefined) => void;
    }) => {
      return (
        <>
          <div className='mb-6'>{builtInFields.map((field) => renderField(field))}</div>

          {draggableFields.length > 0 && (
            <DragContainer
              dragClass='dragging-field'
              lockAxis='y'
              nonDragAreaSelector='.input'
              onDrop={onMove}
              getChildPayload={(index: number): number => index}
              onDragStart={(e: { payload: number }): void => {
                setDraggingIndex(e.payload);
              }}
              onDragEnd={(): void => setDraggingIndex(undefined)}
              getGhostParent={(): HTMLElement => document.body}
            >
              {draggableFields.map((field) => renderField(field))}
            </DragContainer>
          )}
          <div className='mt-4'>{hiddenFields.map((field) => renderField(field))}</div>
        </>
      );
    },
    [renderField]
  );

  return renderFields;
};
